import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Link,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { usefulLinksAPI } from '../services/api';
import { useUsefulLinks } from '../context/UsefulLinksContext';
import { actionsKabobCellSx, actionsKabobHeaderSx } from '../theme/tableStyles';
import ConfirmDialog from '../components/ConfirmDialog';
import RowActionsMenu from '../components/RowActionsMenu';
import {
  tablePageRootSx,
  tablePageHeaderStickySx,
  tablePageScrollRegionSx,
  tablePageTableContainerSx,
} from '../theme/tablePageLayout';
import { toArray } from '../utils/apiData';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import useFieldErrors from '../utils/useFieldErrors';
import { TableSkeletonRows } from '../components/LoadingSkeletons';

const emptyFormData = {
  display_text: '',
  url: '',
};

const UsefulLinksPage = () => {
  const notify = useNotify();
  const fields = useFieldErrors();
  // The sidebar reads the same list from context, so every mutation here has to
  // refresh both this table and the context copy.
  const { loadLinks: reloadSidebarLinks } = useUsefulLinks();

  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [formData, setFormData] = useState(emptyFormData);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadLinkRows = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const response = await usefulLinksAPI.getAll();
      setLinks(toArray(response.data, 'useful links'));
    } catch (err) {
      console.error('Error loading useful links:', err);
      setError(err.response?.data?.error || 'Failed to load useful links');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLinkRows();
  }, [loadLinkRows]);

  const refreshAll = useCallback(async () => {
    await loadLinkRows();
    reloadSidebarLinks();
  }, [loadLinkRows, reloadSidebarLinks]);

  const handleOpenDialog = (link = null) => {
    fields.reset();
    if (link) {
      setEditingLink(link);
      setFormData({
        display_text: link.display_text || '',
        url: link.url || '',
      });
    } else {
      setEditingLink(null);
      setFormData(emptyFormData);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingLink(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    fields.clear(name);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const valid = fields.validate({
      display_text: !formData.display_text.trim() && 'Display text is required',
      url: !formData.url.trim() && 'URL is required',
    });
    if (!valid) return;
    try {
      if (editingLink) {
        await usefulLinksAPI.update(editingLink.id, formData);
      } else {
        await usefulLinksAPI.create(formData);
      }
      handleCloseDialog();
      refreshAll();
    } catch (err) {
      console.error('Error saving useful link:', err);
      notify.error(errorMessage(err, 'Error saving useful link'));
    }
  };

  const handleDeleteClick = (link) => setDeleteTarget(link);
  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    try {
      await usefulLinksAPI.delete(deleteTarget.id);
      setDeleteTarget(null);
      refreshAll();
    } catch (err) {
      console.error('Error deleting useful link:', err);
      notify.error(errorMessage(err, 'Error deleting useful link'));
    }
  };

  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'flex-start', sm: 'space-between' },
            alignItems: 'center',
            gap: { xs: 1.5, sm: 0 },
            mb: 3,
          }}
        >
          <Typography variant="h4">Useful Links</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              minWidth: { xs: 0, sm: 'auto' },
              width: { xs: 32, sm: 'auto' },
              height: { xs: 32, sm: 'auto' },
              px: { xs: 0, sm: 2 },
              '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Add Link
            </Box>
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      <Box sx={tablePageScrollRegionSx}>
        <TableContainer component={Paper} sx={tablePageTableContainerSx}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
                <TableCell>Display Text</TableCell>
                <TableCell>URL</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && <TableSkeletonRows columns={3} />}
              {links.map((link) => (
                <TableRow key={link.id}>
                  <TableCell align="center" sx={actionsKabobCellSx}>
                    <RowActionsMenu
                      actions={[
                        { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => handleOpenDialog(link) },
                        { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => handleDeleteClick(link), color: 'error' },
                      ]}
                    />
                  </TableCell>
                  <TableCell>{link.display_text}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-all' }}>
                    <Link href={link.url} target="_blank" rel="noopener noreferrer">
                      {link.url}
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && links.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No useful links yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingLink ? 'Edit Link' : 'Add New Link'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              size="small"
              name="display_text"
              label="Display Text"
              value={formData.display_text}
              onChange={handleInputChange}
              {...fields.props('display_text')}
              fullWidth
              required
            />
            <TextField
              size="small"
              name="url"
              label="URL"
              value={formData.url}
              onChange={handleInputChange}
              placeholder="https://www.aqha.com"
              {...fields.props('url', 'Leaving off https:// is fine — it will be added for you.')}
              fullWidth
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingLink ? 'Save Changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Link"
        message={`Permanently delete "${deleteTarget?.display_text}"? This cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Box>
  );
};

export default UsefulLinksPage;
